import styled from "styled-components";
import { useRef } from "react";
import { BREAK_POINT } from "../constants";
import useIntersectionObserver from "../utils/useIntersectionObserver";

interface FadeInItemProps {
  children: React.ReactNode;
  height?: number;
  threshold?: number | number[];
  point?: number;
  delay?: number;
  callback?: (
    entry: IntersectionObserverEntry,
    observer: IntersectionObserver
  ) => void;
}

const FadeInItem = ({
  children,
  height = 0,
  threshold = [0, 0.25, 0.5, 0.75, 1],
  point = 0.5,
  delay = 0,
  callback,
}: FadeInItemProps) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useIntersectionObserver(
    ref,
    (entry, observer) => {
      if (entry.intersectionRatio >= point) {
        entry.target.classList.add("active");
        callback && callback(entry, observer);
      } else if (
        entry.target.getBoundingClientRect().top > 0 &&
        entry.intersectionRatio === 0
      ) {
        entry.target.classList.remove("active");
      }
    },
    {
      root: null,
      rootMargin: "0px",
      threshold: threshold,
    }
  );

  return (
    <Wrapper height={height} ref={ref} $delay={delay}>
      {children}
    </Wrapper>
  );
};

export default FadeInItem;

const Wrapper = styled.div<{ height: number; $delay: number }>`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  min-height: ${({ height }) => `${height}px`};
  transform: translate(0, 100px);
  opacity: 0;
  transition-property: opacity, transform, height;
  transition-duration: 1.3s;
  transition-timing-function: cubic-bezier(0.165, 0.84, 0.44, 1);

  &.active {
    opacity: 1;
    transform: translate(0, 0);
  }

  > div {
    width: 100%;
  }

  @media only screen and (max-width: ${BREAK_POINT.tablet}) {
    /* transform: translate(0, 60px); */
    transition-duration: 0.6s;
    ${({ $delay }) => $delay && `transition-delay: ${$delay / 1000}s;`}
  }
`;
